import { useCallback, useEffect, useRef, useState } from "react"
import { Theme } from "@mui/material/styles"
import {
  PdfGeneratorGenerateOptions,
  PdfGeneratorResultDataType,
  PdfGeneratorStatus
} from "../types"
import {
  revokeObjectUrl,
  convertHtmlToPdfBlob,
  renderChildrenToHtmlDocument
} from "../helpers"

export type UsePdfGeneratorOptions = {
  theme?: Theme
  stylesheetUrls?: string[]
  onGenerated?: (result: PdfGeneratorResultDataType) => void
  onError?: (error: unknown) => void
}

export const usePdfGenerator = (args: UsePdfGeneratorOptions = {}) => {
  const {
    theme,
    stylesheetUrls: defaultStylesheetUrls,
    onGenerated,
    onError
  } = args

  const [url, setUrl] = useState<string | null>(null)
  const [blob, setBlob] = useState<Blob | null>(null)
  const [status, setStatus] = useState<PdfGeneratorStatus>("idle")
  const [error, setError] = useState<unknown>(null)
  const [fileName, setFileName] = useState("document.pdf")

  const urlRef = useRef<string | null>(null)
  const lastGenerateOptionsRef = useRef<PdfGeneratorGenerateOptions | null>(
    null
  )

  const clearResult = useCallback(() => {
    revokeObjectUrl(urlRef.current)

    urlRef.current = null

    setUrl(null)
    setBlob(null)
  }, [])

  const generate = useCallback(
    async (
      options: PdfGeneratorGenerateOptions
    ): Promise<PdfGeneratorResultDataType | null> => {
      lastGenerateOptionsRef.current = options

      const {
        content,
        fileName: resolvedFileName,
        documentTitle,
        stylesheetUrls,
        header,
        footer,
        page
      } = options

      setFileName(resolvedFileName)
      setStatus("generating")
      setError(null)
      clearResult()

      try {
        const htmlDocument = renderChildrenToHtmlDocument({
          content,
          documentTitle: documentTitle ?? resolvedFileName,
          theme,
          stylesheetUrls: stylesheetUrls ?? defaultStylesheetUrls,
          header,
          footer,
          page
        })

        const result = await convertHtmlToPdfBlob({
          htmlDocument,
          fileName: resolvedFileName
        })

        urlRef.current = result.url

        setUrl(result.url)
        setBlob(result.blob)
        setStatus("ready")
        onGenerated?.(result)
        return result
      } catch (err) {
        setError(err)
        setStatus("error")
        onError?.(err)

        return null
      }
    },
    [theme, defaultStylesheetUrls, clearResult, onError, onGenerated]
  )

  const retry = useCallback(async () => {
    if (!lastGenerateOptionsRef.current) {
      return null
    }

    return generate(lastGenerateOptionsRef.current)
  }, [generate])

  useEffect(() => {
    return () => {
      revokeObjectUrl(urlRef.current)
    }
  }, [])

  return {
    status,
    blob,
    error,
    fileName,
    downloadUrl: url,
    generate,
    retry
  }
}
